import React from 'react';
import { X, BookOpen, Calculator, Microscope, Star, Clock, User, Calendar, CheckCircle, XCircle, Brain } from 'lucide-react';
import { Database } from '../lib/supabase';

type Activity = Database['public']['Tables']['activities']['Row'];
type AIActivity = Database['public']['Tables']['ai_generated_activities']['Row'];
type ProgressRecord = Database['public']['Tables']['child_progress']['Row'] & {
  children?: { name: string; avatar_color: string };
};

interface ActivityContentViewerProps {
  activity: Activity | AIActivity;
  isAIActivity?: boolean;
  progressRecords?: ProgressRecord[];
  onClose: () => void;
}

const subjectIcons = {
  math: Calculator,
  reading: BookOpen,
  science: Microscope,
};

const subjectColors = {
  math: 'blue',
  reading: 'green',
  science: 'purple',
};

const difficultyLabels = {
  1: 'Beginner',
  2: 'Easy',
  3: 'Medium',
  4: 'Hard',
  5: 'Expert',
};

// Sample questions for regular activities (since they don't have stored content)
const getSampleQuestions = (subject: string, difficulty: number) => {
  const questionSets = {
    math: [
      { question: "What is 5 + 3?", options: ["6", "8", "7", "9"], correct: 1 },
      { question: "What is 12 - 4?", options: ["8", "7", "9", "6"], correct: 0 },
      { question: "What is 3 × 4?", options: ["12", "15", "10", "14"], correct: 0 },
      { question: "What is 16 ÷ 4?", options: ["3", "4", "5", "6"], correct: 1 },
    ],
    reading: [
      { question: "Which word rhymes with 'cat'?", options: ["dog", "hat", "fish", "bird"], correct: 1 },
      { question: "What is the first letter of 'apple'?", options: ["A", "B", "C", "D"], correct: 0 },
      { question: "Which word means the opposite of 'big'?", options: ["large", "huge", "small", "tall"], correct: 2 },
      { question: "How many syllables are in 'butterfly'?", options: ["2", "3", "4", "5"], correct: 1 },
    ],
    science: [
      { question: "What do plants need to grow?", options: ["Only water", "Sunlight and water", "Only soil", "Only air"], correct: 1 },
      { question: "Which animal is a mammal?", options: ["Fish", "Bird", "Dog", "Snake"], correct: 2 },
      { question: "What causes rain?", options: ["Wind", "Clouds", "Sun", "Moon"], correct: 1 },
      { question: "Which planet is closest to the sun?", options: ["Earth", "Mars", "Mercury", "Venus"], correct: 2 },
    ],
  };

  return questionSets[subject as keyof typeof questionSets] || questionSets.math;
};

export function ActivityContentViewer({ activity, isAIActivity = false, progressRecords = [], onClose }: ActivityContentViewerProps) {
  // Safety checks for activity data
  if (!activity) {
    console.warn('ActivityContentViewer: activity prop is undefined or null');
    return null;
  }

  const IconComponent = subjectIcons[activity.subject as keyof typeof subjectIcons] || BookOpen;
  const colorClass = subjectColors[activity.subject as keyof typeof subjectColors] || 'blue';

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getAverageScore = () => {
    if (progressRecords.length === 0) return 0;
    return Math.round(progressRecords.reduce((sum, record) => sum + record.score, 0) / progressRecords.length);
  };

  const getTotalTimeSpent = () => {
    return progressRecords.reduce((sum, record) => sum + record.time_spent, 0);
  };

  // Get activity questions - either from AI content or sample questions
  const getActivityQuestions = () => {
    if (isAIActivity) {
      const aiActivity = activity as AIActivity;
      if (aiActivity.content && typeof aiActivity.content === 'object') {
        const content = aiActivity.content as Record<string, unknown>;
        const questions = content.questions;
        if (Array.isArray(questions)) {
          return questions;
        }
      }
    }
    
    // For regular activities, show sample questions based on subject and difficulty
    return getSampleQuestions(activity.subject, activity.difficulty_level);
  };

  const questions = getActivityQuestions();

  // Simulate question-level results based on overall score
  const getQuestionResults = (record: ProgressRecord) => {
    const numQuestions = questions.length;
    const correctAnswers = Math.round((record.score / 100) * numQuestions);
    
    return questions.map((_, index) => ({
      questionIndex: index,
      isCorrect: index < correctAnswers,
      timeSpent: Math.floor(record.time_spent / numQuestions) + Math.floor(Math.random() * 10),
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className={`bg-gradient-to-r from-${colorClass}-500 to-${colorClass}-600 px-6 py-4 text-white rounded-t-2xl`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <IconComponent className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{activity.title}</h2>
                <div className="flex items-center space-x-4 text-sm opacity-90">
                  <span className="capitalize">{activity.subject}</span>
                  <span>•</span>
                  <span>{difficultyLabels[activity.difficulty_level as keyof typeof difficultyLabels]}</span>
                  <span>•</span>
                  <span>{activity.points_reward} points</span>
                  {isAIActivity && (
                    <>
                      <span>•</span>
                      <span className="flex items-center space-x-1">
                        <Brain className="w-4 h-4" />
                        <span>AI Generated</span>
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Activity Description */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Activity Description</h3>
            <p className="text-gray-700 leading-relaxed">{activity.description}</p>
          </div>

          {/* Activity Questions */}
          {questions.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Activity Questions {isAIActivity ? '' : '(Sample Questions)'}
              </h3>
              <div className="space-y-4">
                {questions.map((question: Record<string, unknown>, index: number) => {
                  const isValidQuestion = question && typeof question.question === 'string';
                  const questionOptions = Array.isArray(question.options) ? question.options : [];
                  const correctIndex = typeof question.correct === 'number' ? question.correct : -1;
                  
                  return (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="font-medium text-gray-900 mb-3">
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 rounded-full text-sm font-bold mr-2">
                          {index + 1}
                        </span>
                        {isValidQuestion ? question.question : 'Question text not available'}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                        {questionOptions.map((option: unknown, optionIndex: number) => {
                          const isCorrect = optionIndex === correctIndex;
                          return (
                            <div
                              key={optionIndex}
                              className={`p-3 rounded-lg border text-sm transition-colors ${
                                isCorrect
                                  ? 'bg-green-100 border-green-300 text-green-800 font-medium'
                                  : 'bg-white border-gray-200 text-gray-700'
                              }`}
                            >
                              <div className="flex items-center space-x-2">
                                <span className="font-semibold">
                                  {String.fromCharCode(65 + optionIndex)}.
                                </span>
                                <span>{String(option) || 'Option not available'}</span>
                                {isCorrect && (
                                  <CheckCircle className="w-4 h-4 text-green-600 ml-auto" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {question.explanation && (
                        <div className="text-sm text-blue-700 bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400">
                          <strong>Explanation:</strong> {String(question.explanation)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Performance Summary */}
          {progressRecords.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">{progressRecords.length}</div>
                  <div className="text-sm text-blue-700">Times Completed</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
                  <div className="text-2xl font-bold text-green-600">{getAverageScore()}%</div>
                  <div className="text-sm text-green-700">Average Score</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center border border-purple-200">
                  <div className="text-2xl font-bold text-purple-600">{formatTime(getTotalTimeSpent())}</div>
                  <div className="text-sm text-purple-700">Total Time</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 text-center border border-yellow-200">
                  <div className="text-2xl font-bold text-yellow-600">
                    {progressRecords.reduce((sum, record) => sum + record.points_earned, 0)}
                  </div>
                  <div className="text-sm text-yellow-700">Total Points</div>
                </div>
              </div>
            </div>
          )}

          {/* Detailed Results */}
          {progressRecords.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Results</h3>
              <div className="space-y-6">
                {progressRecords.map((record, index) => {
                  const questionResults = getQuestionResults(record);
                  
                  return (
                    <div key={record.id} className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
                      {/* Attempt Header */}
                      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
                        <div className="flex items-center space-x-3">
                          {record.children && (
                            <div
                              className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                              style={{ backgroundColor: record.children.avatar_color }}
                            >
                              {record.children.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="font-semibold text-gray-900 text-lg">
                              {record.children?.name || 'Unknown Child'}
                            </div>
                            <div className="text-sm text-gray-600 flex items-center space-x-4">
                              <span className="flex items-center space-x-1">
                                <Calendar className="w-4 h-4" />
                                <span>{new Date(record.completed_at).toLocaleDateString()}</span>
                              </span>
                              <span className="flex items-center space-x-1">
                                <Clock className="w-4 h-4" />
                                <span>{formatTime(record.time_spent)}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className={`inline-flex items-center px-4 py-2 rounded-full text-lg font-bold ${getScoreColor(record.score)}`}>
                            {record.score}%
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            +{record.points_earned} points earned
                          </div>
                        </div>
                      </div>

                      {/* Question-by-Question Results */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Question Results:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {questionResults.map((result, qIndex) => (
                            <div
                              key={qIndex}
                              className={`p-3 rounded-lg border-2 ${
                                result.isCorrect
                                  ? 'bg-green-50 border-green-200'
                                  : 'bg-red-50 border-red-200'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <span className="font-medium text-gray-700">Q{qIndex + 1}</span>
                                  {result.isCorrect ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-red-600" />
                                  )}
                                </div>
                                <span className="text-xs text-gray-500">
                                  {result.timeSpent}s
                                </span>
                              </div>
                              <div className={`text-xs font-medium mt-1 ${
                                result.isCorrect ? 'text-green-700' : 'text-red-700'
                              }`}>
                                {result.isCorrect ? 'Correct' : 'Incorrect'}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Summary for this attempt */}
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">
                              Correct: {questionResults.filter(r => r.isCorrect).length} / {questionResults.length}
                            </span>
                            <span className="text-gray-600">
                              Avg. time per question: {Math.round(record.time_spent / questionResults.length)}s
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* No Results Message */}
          {progressRecords.length === 0 && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-10 h-10 text-gray-400" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">No Results Yet</h4>
              <p className="text-gray-600 text-lg">
                This activity hasn't been completed by any of your children yet.
              </p>
            </div>
          )}

          {/* AI Metadata */}
          {isAIActivity && (activity as AIActivity).ai_metadata && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">AI Generation Details</h3>
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {(() => {
                    const metadata = (activity as AIActivity).ai_metadata as any;
                    return (
                      <>
                        <div>
                          <span className="font-medium text-gray-700">Generated:</span>
                          <span className="ml-2 text-gray-600">
                            {new Date(metadata.generated_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Template:</span>
                          <span className="ml-2 text-gray-600">{metadata.template_used}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Child Age:</span>
                          <span className="ml-2 text-gray-600">{metadata.child_age} years</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Child Level:</span>
                          <span className="ml-2 text-gray-600">Level {metadata.child_level}</span>
                        </div>
                        {metadata.curriculum_target && (
                          <div className="md:col-span-2">
                            <span className="font-medium text-gray-700">Curriculum Target:</span>
                            <span className="ml-2 text-gray-600">{metadata.curriculum_target}</span>
                          </div>
                        )}
                        {metadata.custom_prompt_used && (
                          <div className="md:col-span-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Custom instructions used
                            </span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}